import type { PersonModelInterface } from "@/models/personModel";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import {
    User,
    Phone,
    Hash,
    IdCard,
    MapPin,
    Building2,
    Landmark,
    Users,
} from "lucide-react";

import PersonInfoForms from "@/components/Forms/PersonInfo/PersonInfoForms";

function Row({
                 icon: Icon,
                 label,
                 value,
             }: {
    icon: any;
    label: string;
    value?: React.ReactNode;
}) {
    const empty = value === undefined || value === null || value === "" || value === "—";
    return (
        <div className="flex items-start gap-3 py-2">
            <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
                <Icon className="h-4 w-4 text-muted-foreground" />
            </div>

            <div className="min-w-0 flex-1">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className={`text-sm ${empty ? "text-muted-foreground" : ""}`}>
                    {empty ? "—" : value}
                </div>
            </div>
        </div>
    );
}

export function PersonalInfoCard({
                                     person,
                                     isEditing,
                                 }: {
    person: PersonModelInterface;
    isEditing: boolean;
}) {
    const di: any = person.detailInfo || {};
    const fullName = person.name || [di.firstName, di.secondName, di.lastName].filter(Boolean).join(" ");

    const guarantees: any[] = (person as any).guarantees || [];

    if (isEditing) {
        return (
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Личная информация
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <PersonInfoForms />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Личная информация
                </CardTitle>
            </CardHeader>

            <CardContent className="space-y-3">
                <div className="rounded-lg border bg-muted/20 p-3">
                    <div className="text-xs text-muted-foreground">ФИО</div>
                    <div className="text-base font-semibold leading-tight">{fullName || "—"}</div>
                </div>

                <div className="grid gap-1 md:grid-cols-2">
                    <Row icon={Phone} label="Телефон" value={di.phoneNumber || "—"} />
                    <Row icon={Hash} label="ИНН" value={di.individualNumber || "—"} />
                    <Row icon={IdCard} label="ID паспорта" value={di.idPassport || "—"} />
                    <Row icon={Landmark} label="Город" value={di.city || "—"} />
                    <Row icon={MapPin} label="Адрес" value={di.address || "—"} />
                    <Row icon={Building2} label="Место работы" value={di.placeOfWork || "—"} />
                </div>

                <Separator />

                <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        Поручители
                    </div>

                    {guarantees.length === 0 ? (
                        <div className="text-sm text-muted-foreground">—</div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {guarantees.map((g: any) => (
                                <Badge key={g.id} variant="secondary" className="py-1">
                                    {g.role || "Поручитель"}
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
